import { FC, useEffect, useMemo, useRef } from 'react'

import { APIRequest } from 'models/API'
import { APIRequestState } from 'constants/API'
import { CircularProgress } from '@mui/material'
import { GRAY_900 } from 'constants/styling/theme'
import { logger } from 'utils/logging'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { useTranslation } from 'react-i18next'

/**
 * @interface Props Input properties
 */
interface Props {
  /** Assignment id whose archive download is observed (used for logging context) */
  assignmentId: string
  /** ARCHIVE_VISUALS request state for the archive being prepared (APIRequest-shaped, from React Query) */
  initiateDownloadVisualsArchive: APIRequest<unknown> | null
  /** DOWNLOAD_ARCHIVE request state for the resolved archive job (APIRequest-shaped, from React Query) */
  downloadVisualsArchive: APIRequest<{ url?: string }> | null
  /** Whether a download cycle has been triggered by the user (gates the "preparing" toast) */
  isDownloadTriggered: boolean
}

/**
 * Headless download handler — shows toast notifications and triggers the browser download
 * of the visuals archive. Render it once per gallery so the effects run regardless of
 * which popup (or no popup) is shown.
 *
 * The archive state is provided by the renderer (React Query mutation/query bridged to the
 * APIRequest shape) so the same handler works both inside the client gallery popup and the
 * administrator gallery, each driving its own archive request.
 *
 * @example <DownloadToastHandler assignmentId={assignmentId} initiateDownloadVisualsArchive={initiateDownloadVisualsArchive} downloadVisualsArchive={downloadVisualsArchive} isDownloadTriggered={isDownloadTriggered} />
 */
export const DownloadToastHandler: FC<Props> = ({
  assignmentId,
  initiateDownloadVisualsArchive,
  downloadVisualsArchive,
  isDownloadTriggered
}) => {
  const { t } = useTranslation(['star_rating'])

  const { spawnInfoToast, spawnSuccessToast, spawnErrorToast, removeMessageFromQueue } = useSnackbar()

  const archiveDownloadPending = useMemo(() => {
    if (initiateDownloadVisualsArchive) {
      if (initiateDownloadVisualsArchive.state === APIRequestState.ERROR) return false
      if (downloadVisualsArchive?.state === APIRequestState.ERROR) return false
      if (downloadVisualsArchive?.state === APIRequestState.OK) return false
      return true
    } else return false
  }, [initiateDownloadVisualsArchive, downloadVisualsArchive])

  const downloadStatus = useMemo<'preparing' | 'ready' | 'failed'>(() => {
    if (initiateDownloadVisualsArchive?.state === APIRequestState.ERROR) return 'failed'
    if (downloadVisualsArchive?.state === APIRequestState.ERROR) return 'failed'
    if (downloadVisualsArchive?.state === APIRequestState.OK && downloadVisualsArchive?.data?.url) return 'ready'
    return 'preparing'
  }, [initiateDownloadVisualsArchive, downloadVisualsArchive])

  const downloadUrl = useMemo(() => downloadVisualsArchive?.data?.url, [downloadVisualsArchive?.data?.url])

  // Toast: "preparing" shown while download is pending, dismissed when ready
  useEffect(() => {
    if (isDownloadTriggered && archiveDownloadPending) {
      spawnInfoToast(t('star_rating:download_preparing'), { customId: 'star-rating-preparing', timeout: 0, icon: <CircularProgress size={20} thickness={4} />, messageSx: { fontWeight: 550, color: GRAY_900 } })
    }
    if (!archiveDownloadPending) {
      removeMessageFromQueue('star-rating-preparing')
    }
    // `spawnInfoToast` is intentionally excluded from the deps!!!
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDownloadTriggered, archiveDownloadPending, removeMessageFromQueue])

  // Toast: "ready" + auto-download, triggered once per download cycle
  const downloadTriggeredRef = useRef(false)

  // Guard against stale state of an already completed cycle: only auto-download once a
  // "preparing" phase has been observed during this component's lifetime, otherwise a
  // remount (e.g. revisiting the gallery later in the session) would fire an unsolicited
  // download + success toast.
  const hasObservedPreparingRef = useRef(false)

  // Note: intentionally NOT gated on isDownloadTriggered — once preparation
  // starts, the download must fire even if the client closed the rating modal
  // before the URL arrived (QA-190).
  useEffect(() => {
    if (!hasObservedPreparingRef.current) return

    if (downloadStatus === 'ready' && downloadUrl && !downloadTriggeredRef.current) {
      downloadTriggeredRef.current = true

      // Defensive: downloadUrl is backend-generated, but new URL() throws on a malformed
      // value and a non-https scheme must never reach the href/anchor sinks below
      let parsedUrl: URL | null = null
      try {
        parsedUrl = new URL(downloadUrl)
      } catch {
        parsedUrl = null
      }

      if (!parsedUrl || parsedUrl.protocol !== 'https:') {
        logger.error('Download archive URL invalid', undefined, { context: { component: 'DownloadToastHandler', action: 'auto-download', metadata: { assignmentId, hasUrl: Boolean(downloadUrl) } } })
        spawnErrorToast(t('star_rating:download_failed'), { customId: 'star-rating-failed', messageSx: { fontWeight: 550, color: GRAY_900 } })
        return
      }

      logger.info('Download archive ready', undefined, { context: { component: 'DownloadToastHandler', action: 'auto-download', metadata: { urlHost: parsedUrl.host } } })
      spawnSuccessToast(
        t('star_rating:download_ready'),
        { subtext: <>{t('star_rating:download_ready_subtext_prefix')} <a href={downloadUrl} style={{ textDecoration: 'underline', color: 'inherit' }}>{t('star_rating:download_ready_subtext_link')}</a></>, customId: 'star-rating-ready', timeout: 15000, messageSx: { fontWeight: 550, color: GRAY_900 }, subtextSx: { cursor: 'pointer', fontWeight: 350, color: GRAY_900, opacity: 1 } }
      )
      // Use a hidden anchor to download in the background without navigating away or closing the rating popup
      const a = document.createElement('a')
      a.href = downloadUrl
      a.style.display = 'none'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    }

    if (downloadStatus === 'failed' && !downloadTriggeredRef.current) {
      downloadTriggeredRef.current = true
      logger.error('Download archive failed', initiateDownloadVisualsArchive?.error || downloadVisualsArchive?.error, { context: { component: 'DownloadToastHandler', metadata: { assignmentId, initiateState: initiateDownloadVisualsArchive?.state, downloadState: downloadVisualsArchive?.state, initiateErrorType: initiateDownloadVisualsArchive?.error_type, downloadErrorType: downloadVisualsArchive?.error_type } } })
      spawnErrorToast(t('star_rating:download_failed'), { customId: 'star-rating-failed', messageSx: { fontWeight: 550, color: GRAY_900 } })
    }
    // `spawnSuccessToast` / `spawnErrorToast` are intentionally excluded from the deps — they
    // are not referentially stable (recreated whenever the toast queue changes); this effect
    // must only react to download state changes, the one-shot per cycle is guarded by
    // downloadTriggeredRef
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downloadStatus, downloadUrl, t, assignmentId, initiateDownloadVisualsArchive?.state, initiateDownloadVisualsArchive?.error, initiateDownloadVisualsArchive?.error_type, downloadVisualsArchive?.state, downloadVisualsArchive?.error, downloadVisualsArchive?.error_type])

  // Reset trigger and clear stale toasts when a new download begins mid-session
  useEffect(() => {
    if (downloadStatus === 'preparing') {
      hasObservedPreparingRef.current = true
      downloadTriggeredRef.current = false
      removeMessageFromQueue('star-rating-ready')
      removeMessageFromQueue('star-rating-failed')
    }
  }, [downloadStatus, removeMessageFromQueue])

  // Ensure preparing toast is dismissed if handler unmounts mid-download
  useEffect(() => {
    return () => {
      removeMessageFromQueue('star-rating-preparing')
    }
  }, [removeMessageFromQueue])

  return null
}
